import { ALTERNATIVE, BOND, COMPANY, CUSTOM_SECURITY, INDEX, INDICATOR, MODEL_PORTFOLIO, MUTUAL_FUND, SEPARATE_ACCOUNT, } from 'NewAngular/constants/core.constants';
// Services
import { AjaxService } from 'NewAngular/services/core/ajax.service';
import { UrlsService } from 'NewAngular/services/core/urls.service';
// Utils / Constants
import { INTERSECT_ALL_HOLDINGS } from 'SecuritiesApp/ts/constants/holdings-table.constants';
import { UNDERLYING_HOLDINGS } from 'ModelPortfolioApp/ts/shared/constants';
import * as i0 from "@angular/core";
import * as i1 from "NewAngular/services/core/ajax.service";
import * as i2 from "NewAngular/services/core/urls.service";
export class SecuritiesService {
    constructor(ajaxService, urlsService) {
        this.ajaxService = ajaxService;
        this.urlsService = urlsService;
    }
    getSecuritiesInfo(securityIds) {
        /**
         * `securityIds` represents a string of comma delimited symbols, e.g. "AAPL,GOOG,TSLA" or just "AAPL"
         */
        const urlParams = { securities: securityIds };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SECURITIES_INFO, urlParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    getSecuritiesPerformanceProxyInfo(proxyToSecurityMap) {
        /**
         * `proxyToSecurityMap` represents an object where keys are the
         * performance proxy and the value is the security IDs that the key
         * is proxying. e.g { ^MSACWITR : GOOGL} ^MSACWITR is proxying GOOGL
         */
        const urlParams = { securities: JSON.stringify(proxyToSecurityMap) };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SECURITIES_PERFORMANCE_PROXY_INFO, urlParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    getSecuritiesBenchmarkInfo(securityIds) {
        /**
         * `securityIds` represents a string of comma-delimited symbols, e.g. "AAPL,GOOG,TSLA" or just "AAPL"
         */
        const urlParams = { securities: securityIds };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SECURITIES_AUTO_PERFORMANCE_PROXY_INFO, urlParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    getSecuritiesLevelsInfo(securityIds, performancePeriodicity) {
        /**
         * Given a list of security ids return the security level according to the following logic:
         * For model portfolios return the portfolio levels, for funds return the fund price, otherwise the level will
         * be null.
         * `securityIds` represents a string of comma delimited symbols, e.g. "AAPL,GOOG,TSLA" or just "AAPL"
         */
        const queryParams = performancePeriodicity
            ? { securities: securityIds, performancePeriodicity }
            : { securities: securityIds };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SECURITIES_LEVEL_INFO, undefined, queryParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Given some security IDs, this function retrieves the securities information needed for displaying
     * the securities as part of a watchlist in the edit security watchlist modal.
     * @param securityIds: array of tickers, e.g. ['AAPL', 'GOOG','TSLA']
     * @returns Observable<EditWatchlistModalSecurity[]> with the information for the requested securities
     */
    getSecuritiesInfoForWatchlist(securityIds) {
        const params = { securities: securityIds };
        // We make a POST request because there could be thousands of tickers, and if we pass them as query params in a GET request
        // we will hit the cloudfront URL length limit
        return this.ajaxService.postJsonDataObserveBody(this.urlsService.SECURITIES.SECURITIES_INFO_WATCHLIST, params);
    }
    /**
     * Gets list of all valid holding dates for a security via GET request to `/securities/all_holding_dates/{{ securityId }}`.
     *
     * @param securityId: Security ID.
     */
    getAllHoldingDates(securityId) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.HOLDING_DATES, { securityId });
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Gets All Holdings for a security via GET request to `/securities/all_holdings/{{ securityId }}.json`.
     *
     * @param securityId: Security ID.
     * @param queryParams: object containing additional information for request
     *                     such as holding date, pagination info and sorting info
     */
    getHoldings(securityId, queryParams) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.HOLDINGS, { securityId }, queryParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Builds and returns the URL for exporting all holdings' data to a CSV file.
     *
     * @param securityId: Security ID.
     * @param queryParams: optional objects holding sorting and date params for fetching all holdings data
     */
    getSecurityHoldingsExportUrl(securityId, queryParams) {
        return this.urlsService.getUrl(this.urlsService.SECURITIES.HOLDINGS_EXPORT, { securityId }, queryParams);
    }
    getCommonHoldings(primarySecurityId, queryParams) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.COMMON_HOLDINGS, { primarySecurityId }, queryParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    getCommonHoldingsExportUrl(primarySecurityId, queryParams) {
        return this.urlsService.getUrl(this.urlsService.SECURITIES.COMMON_HOLDINGS_EXPORT, { primarySecurityId }, queryParams);
    }
    /**
     * Makes a request to create a new watchlist based on all the security's holdings.
     *
     * @param securityId: Security ID.
     * @param watchlistName: String with the name for new watchlist.
     * @param useFundTop25: determines whether to create watchlist from M* T25 holdings or lipper All holdings
     * @param holdingDate: optional string holding date to get all holdings data for
     * @param intersectOption - optional string comparable intersect option - all/unique/common
     * @param comparableSecurityIds - optional string[] comparable security ids
     */
    saveHoldingsAsWatchlist(securityId, watchlistName, useFundTop25 = true, holdingDate = '', intersectOption = INTERSECT_ALL_HOLDINGS, comparableSecurityIds = []) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SAVE_HOLDINGS_AS_WATCHLIST, {
            securityId,
        });
        return this.ajaxService.postFormEncodedDataObserveBody(url, {
            watchlistName,
            useFundTop25,
            holdingDate,
            intersectOption,
            comparableSecurityIds,
        });
    }
    /**
     * Gets a FlattenedHoldings object from a GET request to `/securities/flattened_holdings/{{ securityId }}.json`.
     *
     * @param securityId: ID of the model portfolio.
     * @param queryParams: Includes page number, sortBy column, and sort direction.
     */
    getFlattenedHoldings(securityId, queryParams) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.FLATTENED_HOLDINGS, { securityId }, queryParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Builds and returns the URL for exporting flattened holdings' data to a CSV file.
     *
     * @param securityId: ID of the model portfolio.
     * @param queryParams: sortBy and sortDirection information
     */
    getSecurityFlattenedHoldingsExportUrl(securityId, queryParams) {
        return this.urlsService.getUrl(this.urlsService.SECURITIES.FLATTENED_HOLDINGS_EXPORT, { securityId }, queryParams);
    }
    /**
     * Builds and returns the URL for exporting flattened holdings' data to a PDF file.
     *
     * @param securityId: ID of the model portfolio.
     */
    getExpandedHoldingsPDFReportUrl(securityId) {
        return this.urlsService.getUrl(this.urlsService.SECURITIES.EXPANDED_HOLDINGS_PDF_REPORT, { securityId });
    }
    /**
     * Makes a request to create a new watchlist based on the model portfolio's flattened holdings.
     *
     * @param securityId: ID of the model portfolio.
     * @param watchlistName: string with the name for new watchlist.
     * @param intersectOption - optional string comparable intersect option - all/unique/common
     * @param comparableSecurityIds - optional string[] comparable security ids
     */
    saveFlattenedHoldingsAsWatchlist(securityId, watchlistName, intersectOption = INTERSECT_ALL_HOLDINGS, comparableSecurityIds = [], holdingsLayer = UNDERLYING_HOLDINGS) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SAVE_FLATTENED_HOLDINGS_AS_WATCHLIST, {
            securityId,
        });
        return this.ajaxService.postFormEncodedDataObserveBody(url, {
            watchlistName,
            intersectOption,
            comparableSecurityIds,
            holdingsLayer,
        });
    }
    /**
     * Make a request to retrieve security data
     *
     * @param securityType: specified security type
     * @param urlParams: url params
     * @param getParams: GET query params
     */
    getSecurityDataTable(securityType, urlParams, getParams) {
        let urlBase;
        switch (securityType) {
            case COMPANY:
                urlBase = this.urlsService.HISTORICAL_DATA.COMPANY_TABLE;
                break;
            case CUSTOM_SECURITY:
                urlBase = this.urlsService.HISTORICAL_DATA.CUSTOM_SECURITY_TABLE;
                break;
            case INDEX:
                urlBase = this.urlsService.HISTORICAL_DATA.INDEX_TABLE;
                break;
            case INDICATOR:
                urlBase = this.urlsService.HISTORICAL_DATA.INDICATOR_TABLE;
                break;
            case MUTUAL_FUND:
                urlBase = this.urlsService.HISTORICAL_DATA.MUTUAL_FUND_TABLE;
                break;
            case MODEL_PORTFOLIO:
                urlBase = this.urlsService.HISTORICAL_DATA.MODEL_PORTFOLIO_TABLE;
                break;
            case SEPARATE_ACCOUNT:
                urlBase = this.urlsService.HISTORICAL_DATA.SEPARATE_ACCOUNT_TABLE;
                break;
            case BOND:
                urlBase = this.urlsService.HISTORICAL_DATA.BOND_TABLE;
                break;
            case ALTERNATIVE:
                urlBase = this.urlsService.HISTORICAL_DATA.ALTERNATIVE_TABLE;
                break;
            default:
                throw new Error(`${securityType} is not a valid security type for this function.`);
        }
        const url = this.urlsService.getUrl(urlBase, urlParams);
        return this.ajaxService.getDataObserveBody(url, getParams);
    }
    /**
     * Retrieve an url to download historical data
     * @param securityType: specified security type
     * @param urlParams: url param
     */
    getSecurityDataTableExportUrl(securityType, urlParams) {
        let urlBase;
        switch (securityType) {
            case BOND:
                urlBase = this.urlsService.HISTORICAL_DATA.BOND_EXPORT;
                break;
            case COMPANY:
                urlBase = this.urlsService.HISTORICAL_DATA.COMPANY_EXPORT;
                break;
            case CUSTOM_SECURITY:
                urlBase = this.urlsService.HISTORICAL_DATA.CUSTOM_SECURITY_EXPORT;
                break;
            case INDEX:
                urlBase = this.urlsService.HISTORICAL_DATA.INDEX_EXPORT;
                break;
            case INDICATOR:
                urlBase = this.urlsService.HISTORICAL_DATA.INDICATOR_EXPORT;
                break;
            case MUTUAL_FUND:
                urlBase = this.urlsService.HISTORICAL_DATA.MUTUAL_FUND_EXPORT;
                break;
            case MODEL_PORTFOLIO:
                urlBase = this.urlsService.HISTORICAL_DATA.MODEL_PORTFOLIO_EXPORT;
                break;
            case SEPARATE_ACCOUNT:
                urlBase = this.urlsService.HISTORICAL_DATA.SEPARATE_ACCOUNT_EXPORT;
                break;
            case ALTERNATIVE:
                urlBase = this.urlsService.HISTORICAL_DATA.ALTERNATIVE_EXPORT;
                break;
            default:
                throw new Error(`${securityType} is not a valid security type for this function.`);
        }
        return this.urlsService.getUrl(urlBase, urlParams);
    }
    /**
     * Returns a list of related securities for a security.
     *
     * @param securityId: Security ID.
     */
    getRelatedSecurities(securityId) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.DEFAULT_RELATED_SECURITIES, {
            securityId,
        });
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Returns a list of related securities set by the user for a security.
     *
     * @param securityId
     * @param returnDefault
     */
    getUserRelatedSecurities(securityId, returnDefault = false) {
        const queryParams = { returnDefault };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.USER_RELATED_SECURITY_COLLECTION, {
            securityId,
        }, queryParams);
        return this.ajaxService.getDataObserveBody(url);
    }
    getUserRelatedHoldings(securityId) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.USER_RELATED_HOLDING_COLLECTION, {
            securityId,
        });
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Updates a user's related securities list for a security.
     *
     * @param securityId: Security ID.
     * @param relatedSecurityIds: List of related securities IDs.
     */
    putUserRelatedSecurities(securityId, relatedSecurityIds) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.USER_RELATED_SECURITY_COLLECTION, {
            securityId,
        });
        return this.ajaxService.putDataObserveBody(url, { relatedSecurityIds });
    }
    putUserRelatedHoldings(securityId, relatedHoldingIds) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.USER_RELATED_HOLDING_COLLECTION, {
            securityId,
        });
        return this.ajaxService.putDataObserveBody(url, { relatedHoldingIds });
    }
    /**
     * Gets Ticker Tape security information either from specified request or from User Preferences`.
     *
     * @param tickerTapeSecurities: object with selected securities or selected security list
     */
    getTickerTapeSecurities(tickerTapeSecurities) {
        let request = {};
        if (tickerTapeSecurities) {
            request = { ...(tickerTapeSecurities.selected_securitylist || tickerTapeSecurities) };
        }
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.TICKER_TAPE_SECURITIES, {}, request);
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Returns data to populate a chart of top contributors and detractors to a security's overall performance.
     *
     * @param securityId: Security ID.
     * @param performancePeriodicity: Periodicity for which to fetch performance data
     */
    getContributorAndDetractorInfo(securityId, performancePeriodicity, holdingsLayer, asOfDate, asOfDateCustom) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.ATTRIBUTION_CONTRIBUTORS_DETRACTORS, { securityId }, { performancePeriodicity, holdingsLayer, asOfDate, asOfDateCustom });
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Returns data needed to populate a security's Attribution table (absolute).
     *
     * @param securityId: Security ID
     * @param page: Page number
     * @param sortColumn: Column to sort by
     * @param sortDirection: Direction of the sort
     * @param metric: Metric used to calculate contribution
     */
    getAttributionTableData(securityId, page, sortColumn, sortDirection, metric, holdingsLayer, asOfDate, asOfDateCustom) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.ATTRIBUTION_TABLE_DATA, { securityId }, { page, sortColumn, sortDirection, metric, holdingsLayer, asOfDate, asOfDateCustom });
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Returns file (CSV) of data that populates the security's Attribution table (absolute).
     *
     * @param securityId: Security ID
     * @param format: File format (csv)
     * @param sortColumn: Column to sort by
     * @param sortDirection: Direction of the sort
     * @param metric: Metric used to calculate contribution
     * @param title: Title of the file
     */
    getAttributionTableDataExport(securityId, format, sortColumn, sortDirection, metric, holdingsLayer, asOfDate, asOfDateCustom, title = null) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.ATTRIBUTION_TABLE_DATA_EXPORT, {
            securityId,
            format,
        });
        if (format === 'csv') {
            return this.ajaxService.postFormEncodedDataWithTextObserveResponse(url, {
                sortColumn,
                sortDirection,
                metric,
                holdingsLayer,
                asOfDate,
                asOfDateCustom,
            });
        }
        return this.ajaxService.postFormEncodedDataObserveBody(url, {
            sortColumn,
            sortDirection,
            title,
            metric,
            holdingsLayer,
            asOfDate,
            asOfDateCustom,
        });
    }
    /**
     * Returns relative attribution data for an aggregate security.
     *
     * @param securityId: Security ID.
     * @param performancePeriodicity: Periodicity for which to fetch performance data
     */
    getRelativeAttributionData(securityId, performancePeriodicity, benchmarkOption, customBenchmarkId, categorization, asOfDate, asOfDateCustom) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.RELATIVE_ATTRIBUTION, { securityId }, { performancePeriodicity, benchmarkOption, customBenchmarkId, categorization, asOfDate, asOfDateCustom });
        return this.ajaxService.getDataObserveBody(url);
    }
    /**
     * Returns relative attribution data pdf export for an aggregate security.
     *
     * @param securityId: Security ID.
     * @param performancePeriodicity: Periodicity for which to fetch performance data
     */
    getRelativeAttributionDataExport(securityId, title, performancePeriodicity, benchmarkOption, customBenchmarkId, categorization, asOfDate, asOfDateCustom) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.RELATIVE_ATTRIBUTION_EXPORT, { securityId });
        return this.ajaxService.postFormEncodedDataObserveBody(url, {
            performancePeriodicity,
            benchmarkOption,
            customBenchmarkId,
            categorization,
            asOfDate,
            asOfDateCustom,
            title,
        });
    }
    /**
     * Returns Factor Attribution data for an aggregate security.
     */
    getFactorAttributionData(securityId, performancePeriodicity, attributionMode) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.FACTOR_ATTRIBUTION, { securityId }, { performancePeriodicity, attributionMode });
        return this.ajaxService.getDataObserveBody(url);
    }
    getFactorAttributionDataExport(securityId, title, performancePeriodicity, attributionMode) {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.FACTOR_ATTRIBUTION_EXPORT, { securityId });
        return this.ajaxService.postFormEncodedDataObserveBody(url, { performancePeriodicity, attributionMode, title });
    }
}
SecuritiesService.ɵfac = function SecuritiesService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || SecuritiesService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.UrlsService)); };
SecuritiesService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SecuritiesService, factory: SecuritiesService.ɵfac, providedIn: 'root' });
